import React, { useState } from "react";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import { AxiosError, AxiosInstance } from "axios";
import { useMutation, useQueryClient } from "react-query";

import { Risk } from "components/Risk/types";
import { useClient } from "utils/client";
import { Modal } from "components/Modal/Modal";
import RadioInput from "components/inputs/RadioInput";
import SelectInput from "components/inputs/SelectInput";
import { useRiskTemplates } from "pages/Risk/hooks/useRisks";
import { useRiskTemplates as useCustomRiskTemplates } from "components/RiskTemplates/hooks/useRiskTemplates";
import { PrimaryButton } from "components/Button/PrimaryButton";
import i18n from "i18n";
import { Feature, useFeatureFlags } from "context/FeatureFlag";
import TextInput from "components/inputs/TextInput";
import TextArea from "components/inputs/TextAreaInput";

interface CreateRiskTemplateOnDashboardProps {
  button: React.FunctionComponent<React.ButtonHTMLAttributes<any>>;
}

export const CreateRiskTemplateOnDashboard: React.FunctionComponent<
  CreateRiskTemplateOnDashboardProps
> = (props) => {
  const [modalVisible, setModalVisible] = React.useState(false);

  return (
    <>
      <div>
        <props.button onClick={() => setModalVisible(true)} />
      </div>

      <>
        <Modal
          onClose={() => setModalVisible(false)}
          show={modalVisible}
          width="1/2"
          content={<CreateRiskTemplateForm />}
        />
      </>
    </>
  );
};

export function capitalizeFirstLetter(str: string): string {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const templatesToDisplay = (
  templates?: { label: string; value: string }[]
) =>
  templates
    ?.sort((a, b) => a.label.localeCompare(b.label))
    ?.map((t) => ({
      ...t,
      label: capitalizeFirstLetter(t.label),
    })) || [];
enum CreateOption {
  copy = "copy",
  scratch = "scratch",
}

export const createRiskTemplate = async (
  client: AxiosInstance,
  payload: Risk | { blueprint_name: string; blueprint_description: string }
) => {
  try {
    const { data } = await client.post("/risk-templates", { payload });
    return data;
  } catch (error) {
    console.error(error);
    throw new Error((error as AxiosError).response?.data);
  }
};

export const useCreateRiskTemplate = () => {
  const client = useClient();
  const queryClient = useQueryClient();
  const history = useHistory();
  const { mutate, isLoading } = useMutation(
    async (
      payload: Risk | { blueprint_name: string; blueprint_description: string }
    ) => await createRiskTemplate(client, payload),
    {
      onSuccess: async (riskTemplate) => {
        await queryClient.refetchQueries(["riskTemplates"]);
        history.push(`/risk-templates/${riskTemplate.crn}`);
      },
    }
  );

  return { createRiskTemplate: mutate, isLoading };
};

const CreateRiskTemplateForm = () => {
  const { t } = useTranslation();
  const { risks: certainTemplates } = useRiskTemplates();
  const { riskTemplates: customTemplates } = useCustomRiskTemplates();
  const language = i18n.language.split("-")[0].toUpperCase();
  const { createRiskTemplate, isLoading } = useCreateRiskTemplate();
  const [selectedOption, setSelectedOption] = useState<CreateOption>(
    CreateOption.scratch
  );
  const [selectedRisk, setSelectedRisk] = useState<Risk>();
  const [blueprintName, setBlueprintName] = useState<string>("");
  const [blueprintDescription, setBlueprintDescription] = useState<string>("");

  const langRegex = new RegExp(`\\[${language}\\]`, "g");

  const templates = [
    ...customTemplates.map((t) => t.payload),
    ...(certainTemplates || [])?.filter((template) =>
      template.blueprint_name?.match(langRegex)
    ),
  ];

  const handleCreateRiskTemplate = () => {
    if (selectedOption === CreateOption.copy && selectedRisk) {
      // Create from existing risk but overwrite blueprint name and description
      createRiskTemplate({
        ...selectedRisk,
        blueprint_name: blueprintName,
        blueprint_description: blueprintDescription,
        mitigations: selectedRisk.mitigations || [{}],
      });
    } else if (selectedOption === CreateOption.scratch) {
      // Create from scratch with only blueprint name and description
      createRiskTemplate({
        blueprint_name: blueprintName,
        blueprint_description: blueprintDescription,
        // @ts-ignore
        mitigations: [{}],
      });
    }
  };

  const options = [
    {
      label: t(`riskTemplate.create.options.${CreateOption.scratch}`),
      value: CreateOption.scratch,
    },
    {
      label: t(`riskTemplate.create.options.${CreateOption.copy}`),
      value: CreateOption.copy,
    },
  ];

  return (
    <div>
      <span className="text-xl">{t("riskTemplate.create.create_new")}</span>
      <div className="pt-4 pb-4">
        <RadioInput
          islast
          required
          id="createOption"
          name="createOption"
          label={t("riskTemplate.create.create_choice")}
          defaultValue={selectedOption}
          onBlur={(option) =>
            setSelectedOption(option.target.value as CreateOption)
          }
          options={options}
        />

        {/* Template fields (always visible) */}
        <TextInput
          required
          id="templateName"
          name="templateName"
          defaultValue={blueprintName}
          label={t("riskTemplate.create.name")}
          uncontrolled
          // @ts-ignore
          onChange={(e) => setBlueprintName(e.target.value)}
          placeholder={t("riskTemplate.create.name_placeholder")}
        />

        <TextArea
          required
          id="templateDescription"
          name="templateDescription"
          defaultValue={blueprintDescription}
          label={t("riskTemplate.create.description")}
          uncontrolled
          // @ts-ignore
          onChange={(e) => setBlueprintDescription(e.target.value)}
        />

        {/* Risk selection (only visible when creating from existing risk) */}
        {selectedOption === CreateOption.copy && (
          <>
            <p className="mt-2 text-sm text-gray-500"></p>
            <SelectInput
              required
              id="risk"
              name="risk"
              defaultValue={""}
              label={t("riskTemplate.create.existing_risk")}
              onBlur={(option) =>
                setSelectedRisk(
                  templates!.find((t) => t.resource_id === option.target.value)!
                )
              }
              options={
                templatesToDisplay(
                  templates?.map((template) => ({
                    value: template.resource_id,
                    label: `${t(
                      "form.risk.options.group." + template?.group_id,
                      {
                        defaultValue: t("riskTemplate.selection.noGroup"),
                      }
                    )} - ${template.blueprint_name!.replace(
                      langRegex,
                      "CERTAIN - "
                    )}`,
                  }))
                ) || []
              }
            />
          </>
        )}
      </div>

      <PrimaryButton
        float="right"
        disabled={
          !blueprintName ||
          !blueprintDescription ||
          (selectedOption === CreateOption.copy && !selectedRisk) ||
          isLoading
        }
        onClick={handleCreateRiskTemplate}
      >
        {isLoading
          ? t("riskTemplate.create.cta_loading")
          : t("riskTemplate.create.cta")}
      </PrimaryButton>
    </div>
  );
};
